import {Toaster} from '@/components/ui/sonner';
import {TooltipProvider} from '@/components/ui/tooltip';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {BrowserRouter, Routes, Route, Navigate} from 'react-router-dom';
import {AuthProvider} from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Auth Pages
import Login from './pages/Login';
import VerifyOTP from './pages/VerifyOTP';
import RequestAgency from './pages/RequestAgency';

// Main Pages
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import LicenseCollectRequestsPage from './pages/LicenseCollectRequests';
import Orders from './pages/Orders';
import Wallet from './pages/Wallet';
import Licenses from './pages/Licenses';
import Invoices from './pages/Invoices';
import ProfileConfirm from './pages/ProfileConfirm';
import NotFound from './pages/NotFound';
import Support from '@/pages/Support';
import NotificationsPage from '@/pages/Notifications';

const queryClient = new QueryClient();

// App.tsx
function ErrorBoundary({children}: { children: React.ReactNode }) {
    return (
        <React.ErrorBoundary
            fallbackRender={({error}) => <pre className="p-4 text-red-600">{String(error)}</pre>}
        >
            {children}
        </React.ErrorBoundary>
    );
}

const App = () => (
    <QueryClientProvider client={queryClient}>
        <TooltipProvider>
            <Toaster/>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/login" element={<Login/>}/>
                        <Route path="/verify-otp" element={<VerifyOTP/>}/>
                        <Route path="/request-agency" element={<RequestAgency/>}/>

                        {/* Protected Routes */}
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <ProfileConfirm/> {/* صفحه‌ای که اطلاعات آدرس و مجتمع و ... رو نهایی می‌کند */}
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <Dashboard/>
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />

                        <Route path="/products" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Products/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/orders" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Orders/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/orders/:id" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Orders/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/support" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Support/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/wallet" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Wallet/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/licenses" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Licenses/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/invoices" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Invoices/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route path="/invoices/:id" element={
                            <ProtectedRoute>
                                <Layout>
                                    <Invoices/>
                                </Layout>
                            </ProtectedRoute>
                        }/>
                        <Route
                            path="/notifications"
                            element={
                                <ProtectedRoute>
                                    <Layout>
                                        <NotificationsPage/>
                                    </Layout>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/reject" element={
                            <ProtectedRoute>
                                <Layout>
                                    <LicenseCollectRequestsPage/>
                                </Layout>
                            </ProtectedRoute>
                        }/>

                        {/* Redirects */}
                        <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
                        <Route path="*" element={<NotFound/>}/>
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </TooltipProvider>
    </QueryClientProvider>
);

export default App;
